/**
 * Module quản lý phím tắt chuyên dụng khi phát video (Video Hotkey Handler)
 * 
 * Hỗ trợ điều khiển video siêu tiện lợi bằng bàn phím:
 * 1. Phím Space: Tạm dừng / Tiếp tục phát video (Pause / Play)
 * 2. Phím < hoặc , hoặc ArrowLeft hoặc J: Tua lùi lại 10 giây (Prev 10s)
 * 3. Phím > hoặc . hoặc ArrowRight hoặc L: Tua tiến tới 10 giây (Next 10s)
 * 4. Phím M: Bật / Tắt tiếng (Mute / Unmute)
 * 5. Phím F: Phóng to toàn màn hình / Thu nhỏ lại (Fullscreen Toggle)
 * 
 * Có kèm HUD Overlay trực quan (Hiển thị biểu tượng ⏪ -10s, ⏩ +10s, ⏸, ▶ giữa màn hình)
 * Tự động bỏ qua khi đang gõ chữ trong ô tìm kiếm hoặc đang ở giao diện đọc truyện.
 */

export class VideoHotkeyHandler {
  /**
   * @param {import('./videoPlayerEngine.js').VideoPlayerEngine} playerEngine 
   * @param {Object} [controllers]
   * @param {import('./capcutController.js').CapcutController} [controllers.capcutController]
   * @param {import('./canvaController.js').CanvaController} [controllers.canvaController]
   */
  constructor(playerEngine, controllers = {}) {
    this.engine = playerEngine;
    this.capcutController = controllers.capcutController || null;
    this.canvaController = controllers.canvaController || null;

    this.bindEvents();
  }

  /**
   * Đăng ký lắng nghe sự kiện phím tắt toàn cục
   */
  bindEvents() {
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  /**
   * Kiểm tra người dùng có đang gõ chữ trong ô input, textarea không
   * @returns {boolean}
   */
  isInputActive() {
    const active = document.activeElement;
    if (!active) return false;
    const tag = active.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || active.isContentEditable;
  }

  /**
   * Kiểm tra xem hiện tại có đang ở chế độ xem video hay không
   * @returns {boolean}
   */
  isVideoActive() {
    // 1. Kiểm tra qua AppRouter nếu có
    if (window.appRouter && window.appRouter.currentMode === 'video') {
      return true;
    }

    // 2. Kiểm tra xem màn hình CapCut hoặc Canva có đang hiển thị class active
    const isCapcutActive = document.getElementById('view-capcut')?.classList.contains('active');
    const isCanvaActive = document.getElementById('view-canva')?.classList.contains('active');
    if (isCapcutActive || isCanvaActive) {
      return true;
    }

    // 3. Hoặc video đang phát ngầm
    return !!(this.engine && this.engine.isPlaying);
  }

  /**
   * Xử lý sự kiện bấm phím tắt điều khiển video
   * @param {KeyboardEvent} e 
   */
  handleKeyDown(e) {
    // Nếu người dùng đang gõ tìm kiếm hoặc không ở chế độ video: Bỏ qua
    if (this.isInputActive() || !this.isVideoActive()) {
      return;
    }

    // Bỏ qua nếu đang giữ phím bổ trợ Alt hoặc Ctrl/Meta (trừ phím tắt hệ thống khác)
    if (e.altKey || e.ctrlKey || e.metaKey) {
      return;
    }

    const key = e.key;

    // -----------------------------------------------------------------
    // 1. PHÍM SPACE: TẠM DỪNG / TIẾP TỤC PHÁT (PLAY / PAUSE)
    // -----------------------------------------------------------------
    if (key === ' ' || e.code === 'Space') {
      e.preventDefault();
      e.stopPropagation();

      // Kiểm tra trạng thái phát trước khi toggle để hiển thị HUD chính xác
      const willPlay = !this.engine.isPlaying;
      this.engine.togglePlay();
      this.showHudToast(willPlay ? '▶ Đang phát' : '⏸ Tạm dừng');
      return;
    }

    // -----------------------------------------------------------------
    // 2. PHÍM LÙI LẠI 10 GIÂY (PREV 10s): Phím <, ,, Mũi tên trái, hoặc J
    // -----------------------------------------------------------------
    const isRewindKey = (
      key === '<' ||
      key === ',' ||
      key === 'ArrowLeft' ||
      key === 'j' ||
      key === 'J'
    );
    if (isRewindKey) {
      e.preventDefault();
      e.stopPropagation();

      this.engine.seekRelative(-10);
      this.showHudToast('⏪ -10s');
      return;
    }

    // -----------------------------------------------------------------
    // 3. PHÍM TIẾN TỚI 10 GIÂY (NEXT 10s): Phím >, ., Mũi tên phải, hoặc L
    // -----------------------------------------------------------------
    const isForwardKey = (
      key === '>' ||
      key === '.' ||
      key === 'ArrowRight' ||
      key === 'l' ||
      key === 'L'
    );
    if (isForwardKey) {
      e.preventDefault();
      e.stopPropagation();

      this.engine.seekRelative(10);
      this.showHudToast('⏩ +10s');
      return;
    }

    // -----------------------------------------------------------------
    // 4. PHÍM M: BẬT / TẮT TIẾNG (MUTE / UNMUTE)
    // -----------------------------------------------------------------
    if (key === 'm' || key === 'M') {
      e.preventDefault();
      e.stopPropagation();

      if (this.engine.isMuted) {
        this.engine.unMute();
        this.showHudToast('🔊 Đã bật tiếng');
      } else {
        this.engine.mute();
        this.showHudToast('🔇 Đã tắt tiếng');
      }
      return;
    }

    // -----------------------------------------------------------------
    // 5. PHÍM F: PHÓNG TO / THU NHỎ TOÀN MÀN HÌNH (FULLSCREEN)
    // -----------------------------------------------------------------
    if (key === 'f' || key === 'F') {
      e.preventDefault();
      e.stopPropagation();

      const isCanva = document.getElementById('view-canva')?.classList.contains('active');
      if (isCanva && this.canvaController) {
        this.canvaController.toggleFullscreen();
      } else if (this.capcutController) {
        this.capcutController.toggleFullscreen();
      }
      return;
    }
  }

  /**
   * Hiển thị bảng thông báo nhanh (HUD Toast) giữa khung xem video
   * @param {string} message 
   */
  showHudToast(message) {
    // Xác định khung video đang hiển thị (CapCut hay Canva)
    const isCanva = document.getElementById('view-canva')?.classList.contains('active');
    const container = isCanva 
      ? document.getElementById('cv-player-box') 
      : document.getElementById('cc-player-box');
    
    if (!container) return;

    let hud = container.querySelector('.video-action-hud');
    if (!hud) {
      hud = document.createElement('div');
      hud.className = 'video-action-hud';
      container.appendChild(hud);
    }

    hud.textContent = message;
    hud.classList.remove('active');
    // Trigger DOM reflow để kích hoạt hiệu ứng mượt mà
    void hud.offsetWidth;
    hud.classList.add('active');

    if (hud.__timer) clearTimeout(hud.__timer);
    hud.__timer = setTimeout(() => {
      hud.classList.remove('active');
    }, 700);
  }
}
